import { useEffect, useRef, useCallback } from 'react';

export type WsMessage =
    | { type: 'init'; notif_count: number; claims_count?: number }
    | { type: 'notif_inc' }
    | { type: 'claims_inc' }
    | { type: 'claims_dec' };

function getWsBase(): string {
    // Prefer explicit VITE_SOCKET_URL, fall back to deriving from VITE_API_URL
    const socketUrl = import.meta.env.VITE_SOCKET_URL as string | undefined;
    if (socketUrl) return socketUrl;
    const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
    if (apiUrl) return apiUrl.replace(/^http/, 'ws').replace(/\/api$/, '');
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://localhost:6002`;
}

function getToken(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)crm_auth_token=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
}

export function useWebSocket(onMessage: (msg: WsMessage) => void): void {
    const enabled = import.meta.env.VITE_SOCKET_ENABLED !== 'false';

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const connect = useCallback(() => {
        if (!mountedRef.current || !enabled) return;

        const token = getToken();
        if (!token) return;

        const url = `${getWsBase()}/api/ws?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data) as WsMessage;
                onMessageRef.current(msg);
            } catch { /* ignore malformed */ }
        };

        ws.onclose = () => {
            wsRef.current = null;
            if (!mountedRef.current) return;
            // Reconnect after 5s
            reconnectRef.current = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
            ws.close();
        };
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        connect();
        return () => {
            mountedRef.current = false;
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            wsRef.current?.close();
        };
    }, [connect]);
}
